// src/features/privateAlbum/service/privateAlbumService.js

import {
  db,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  limit,
} from "../../../config/firebase";
import {
  ALBUM_STATUS,
  ACCESS_STATUS,
  ERROR_MESSAGES,
} from "../types/privateAlbumTypes";
import {
  generateAlbumCode,
  validateAlbumData,
  isAlbumAccessible,
  getRolePermissions,
} from "../utils/privateAlbumHelpers";

class PrivateAlbumService {
  // Collection references
  getAlbumCollection() {
    return collection(db, "privateAlbums");
  }

  getAccessCollection() {
    return collection(db, "albumAccess");
  }

  getPhotoCollection() {
    return collection(db, "albumPhotos");
  }

  getAccessLogCollection() {
    return collection(db, "albumAccessLogs");
  }

  /**
   * Convert Firebase Timestamp to ISO string
   */
  convertTimestamp(value) {
    if (!value) return null;
    if (typeof value === "object" && value.seconds !== undefined) {
      return new Date(
        value.seconds * 1000 + (value.nanoseconds || 0) / 1000000,
      ).toISOString();
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    return value;
  }

  /**
   * Convert document data - convert all timestamps to strings
   */
  convertDocData(docData) {
    if (!docData) return docData;

    const converted = { ...docData };

    // Convert common timestamp fields
    const timestampFields = [
      "createdAt",
      "updatedAt",
      "invitedAt",
      "lastAccessed",
      "uploadedAt",
      "sentAt",
      "viewedAt",
      "paymentDate",
      "expiresAt",
      "timestamp",
      "eventDate",
    ];

    timestampFields.forEach((field) => {
      if (converted[field]) {
        converted[field] = this.convertTimestamp(converted[field]);
      }
    });

    // Handle nested objects that might have timestamps
    if (converted.accessList) {
      converted.accessList = converted.accessList.map((access) =>
        this.convertDocData(access),
      );
    }

    if (converted.photos) {
      converted.photos = converted.photos.map((photo) =>
        this.convertDocData(photo),
      );
    }

    return converted;
  }

  // ============ ALBUM CRUD OPERATIONS ============

  /**
   * Get all albums with optional filters
   */
  async getAlbums(filters = {}) {
    try {
      let q = query(this.getAlbumCollection(), orderBy("createdAt", "desc"));

      if (filters.status && filters.status !== "all") {
        q = query(q, where("status", "==", filters.status));
      }

      if (filters.eventType && filters.eventType !== "all") {
        q = query(q, where("eventType", "==", filters.eventType));
      }

      if (filters.clientEmail) {
        q = query(q, where("clientEmail", "==", filters.clientEmail));
      }

      // Search filter (simple implementation)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const snapshot = await getDocs(q);
        const albums = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (
            data.title?.toLowerCase().includes(searchLower) ||
            data.clientNames?.toLowerCase().includes(searchLower) ||
            data.description?.toLowerCase().includes(searchLower)
          ) {
            albums.push({
              id: doc.id,
              ...this.convertDocData(data),
            });
          }
        });
        return albums;
      }

      const querySnapshot = await getDocs(q);
      const albums = [];
      querySnapshot.forEach((doc) => {
        albums.push({
          id: doc.id,
          ...this.convertDocData(doc.data()),
        });
      });

      return albums;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get album by ID with related data
   */
  async getAlbumById(id, includePhotos = true, includeAccess = true) {
    try {
      const docRef = doc(db, "privateAlbums", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Album not found");
      }

      const album = this.convertDocData({
        id: docSnap.id,
        ...docSnap.data(),
      });

      // Check if album is accessible
      if (!isAlbumAccessible(album)) {
        throw new Error("Album is not accessible");
      }

      // Fetch photos if requested
      if (includePhotos) {
        const photos = await this.getAlbumPhotos(id);
        album.photos = photos;
        album.totalPhotos = photos.length;
      }

      // Fetch access list if requested
      if (includeAccess) {
        const accessList = await this.getAlbumAccess(id);
        album.accessList = accessList;
      }

      return album;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a new album
   */
  async createAlbum(data) {
    try {
      // Validate data
      const errors = validateAlbumData(data);
      if (Object.keys(errors).length > 0) {
        throw new Error(Object.values(errors)[0]);
      }

      // Get current user email from data or use a default
      const createdBy =
        data.createdBy || data.ownerEmail || "admin@example.com";

      const albumData = {
        ...data,
        code: generateAlbumCode(data.title, data.clientNames),
        status: ALBUM_STATUS.ACTIVE,
        totalPhotos: 0,
        totalSize: 0,
        accessCount: 0,
        viewCount: 0,
        tags: data.tags || [],
        createdBy: createdBy,
        ownerEmail: data.ownerEmail || createdBy,
        // Cover image fields - initially empty
        coverImageUrl: data.coverImageUrl || null,
        coverThumbnailUrl: data.coverThumbnailUrl || null,
        settings: {
          allowDownload: data.settings?.allowDownload ?? true,
          allowComments: data.settings?.allowComments ?? true,
          watermark: data.settings?.watermark ?? false,
          passwordProtected: data.settings?.passwordProtected ?? false,
          password: data.settings?.password || null,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(this.getAlbumCollection(), albumData);
      const newDoc = await getDoc(docRef);

      // Auto-share with client
      if (data.clientEmail) {
        try {
          await this.shareAlbum(
            docRef.id,
            data.clientEmail,
            data.clientNames || "Client",
            "viewer",
            createdBy,
          );
        } catch (shareError) {
          console.error("Failed to auto-share album:", shareError);
        }
      }

      return {
        id: newDoc.id,
        ...this.convertDocData(newDoc.data()),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update album
   */
  async updateAlbum(id, data) {
    try {
      const docRef = doc(db, "privateAlbums", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Album not found");
      }

      const updateData = {
        ...data,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(docRef, updateData);
      const updatedDoc = await getDoc(docRef);

      return {
        id: updatedDoc.id,
        ...this.convertDocData(updatedDoc.data()),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete album
   */
  async deleteAlbum(id) {
    try {
      // Delete album
      const albumRef = doc(db, "privateAlbums", id);
      await deleteDoc(albumRef);

      // Delete all photos
      const photos = await this.getAlbumPhotos(id);
      const batch = writeBatch(db);
      photos.forEach((photo) => {
        const photoRef = doc(db, "albumPhotos", photo.id);
        batch.delete(photoRef);
      });
      await batch.commit();

      // Delete all access entries
      const accessList = await this.getAlbumAccess(id);
      const accessBatch = writeBatch(db);
      accessList.forEach((access) => {
        const accessRef = doc(db, "albumAccess", access.id);
        accessBatch.delete(accessRef);
      });
      await accessBatch.commit();

      // Delete all logs
      const logs = await this.getAccessLogs(id);
      const logBatch = writeBatch(db);
      logs.forEach((log) => {
        const logRef = doc(db, "albumAccessLogs", log.id);
        logBatch.delete(logRef);
      });
      await logBatch.commit();

      return id;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Archive album
   */
  async archiveAlbum(id) {
    try {
      const docRef = doc(db, "privateAlbums", id);
      await updateDoc(docRef, {
        status: ALBUM_STATUS.ARCHIVED,
        updatedAt: serverTimestamp(),
      });
      return await this.getAlbumById(id);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Restore archived album
   */
  async restoreAlbum(id) {
    try {
      const docRef = doc(db, "privateAlbums", id);
      await updateDoc(docRef, {
        status: ALBUM_STATUS.ACTIVE,
        updatedAt: serverTimestamp(),
      });
      return await this.getAlbumById(id);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ PHOTO MANAGEMENT ============

  /**
   * Get all photos for an album
   */
  async getAlbumPhotos(albumId) {
    try {
      const q = query(
        this.getPhotoCollection(),
        where("albumId", "==", albumId),
        where("isDeleted", "==", false),
        orderBy("order", "asc"),
      );
      const querySnapshot = await getDocs(q);
      const photos = [];
      querySnapshot.forEach((doc) => {
        photos.push({
          id: doc.id,
          ...this.convertDocData(doc.data()),
        });
      });
      return photos;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload photos to album - UPDATED to set cover in album document
   */
  async uploadPhotos(albumId, photosData, uploadedBy) {
    try {
      const albumRef = doc(db, "privateAlbums", albumId);
      const albumSnap = await getDoc(albumRef);

      if (!albumSnap.exists()) {
        throw new Error("Album not found");
      }

      const album = albumSnap.data();
      const batch = writeBatch(db);
      let totalSize = 0;
      let uploadedCount = 0;

      // Get current max order
      const existingPhotos = await this.getAlbumPhotos(albumId);
      const maxOrder = existingPhotos.length;
      const isFirstPhoto = existingPhotos.length === 0;

      // Upload each photo
      photosData.forEach((photoData, index) => {
        const photoRef = doc(this.getPhotoCollection());
        const isCover = isFirstPhoto && index === 0;

        const photoDoc = {
          albumId: albumId,
          url: photoData.url,
          thumbnailUrl: photoData.thumbnailUrl || photoData.url,
          storagePath: photoData.storagePath || "",
          thumbnailStoragePath: photoData.thumbnailStoragePath || "",
          fileName: photoData.fileName,
          fileSize: photoData.fileSize || 0,
          width: photoData.width || 0,
          height: photoData.height || 0,
          uploadedBy: uploadedBy || "unknown",
          uploadedAt: serverTimestamp(),
          order: maxOrder + index,
          caption: photoData.caption || "",
          isCover: isCover,
          isDeleted: false,
          views: 0,
          likes: 0,
          comments: [],
        };

        batch.set(photoRef, photoDoc);
        totalSize += photoData.fileSize || 0;
        uploadedCount++;
      });

      // Update album stats
      const newTotalPhotos = (album.totalPhotos || 0) + uploadedCount;
      const newTotalSize = (album.totalSize || 0) + totalSize;

      // Prepare update data
      const updateData = {
        totalPhotos: newTotalPhotos,
        totalSize: newTotalSize,
        updatedAt: serverTimestamp(),
      };

      // If first photo, set as cover in album document
      if (isFirstPhoto && photosData.length > 0) {
        const firstPhoto = photosData[0];
        updateData.coverImageUrl = firstPhoto.url;
        updateData.coverThumbnailUrl =
          firstPhoto.thumbnailUrl || firstPhoto.url;
      }

      batch.update(albumRef, updateData);
      await batch.commit();

      return {
        uploaded: uploadedCount,
        totalSize: newTotalSize,
        totalPhotos: newTotalPhotos,
        coverUpdated: isFirstPhoto,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete photo - UPDATED to clear cover if deleted photo was cover
   */
  async deletePhoto(photoId) {
    try {
      const photoRef = doc(db, "albumPhotos", photoId);
      const photoSnap = await getDoc(photoRef);

      if (!photoSnap.exists()) {
        throw new Error("Photo not found");
      }

      const photo = photoSnap.data();
      const wasCover = photo.isCover;

      // Soft delete
      await updateDoc(photoRef, {
        isDeleted: true,
        updatedAt: serverTimestamp(),
      });

      // Update album stats
      const albumRef = doc(db, "privateAlbums", photo.albumId);
      const albumSnap = await getDoc(albumRef);
      if (albumSnap.exists()) {
        const album = albumSnap.data();
        const updateData = {
          totalPhotos: Math.max(0, (album.totalPhotos || 0) - 1),
          totalSize: Math.max(
            0,
            (album.totalSize || 0) - (photo.fileSize || 0),
          ),
          updatedAt: serverTimestamp(),
        };

        // If the deleted photo was the cover, clear cover fields
        if (wasCover) {
          updateData.coverImageUrl = null;
          updateData.coverThumbnailUrl = null;
        }

        await updateDoc(albumRef, updateData);
      }

      return photoId;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update photo details
   */
  async updatePhoto(photoId, data) {
    try {
      const photoRef = doc(db, "albumPhotos", photoId);
      await updateDoc(photoRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });
      const updatedDoc = await getDoc(photoRef);
      return {
        id: updatedDoc.id,
        ...this.convertDocData(updatedDoc.data()),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Set cover photo - UPDATED to update album document
   */
  async setCoverPhoto(albumId, photoId) {
    try {
      const batch = writeBatch(db);

      // Get the photo to use its URL
      const photoRef = doc(db, "albumPhotos", photoId);
      const photoSnap = await getDoc(photoRef);

      if (!photoSnap.exists()) {
        throw new Error("Photo not found");
      }

      const photo = photoSnap.data();

      // Remove current cover from all photos
      const photos = await this.getAlbumPhotos(albumId);
      photos.forEach((p) => {
        if (p.isCover) {
          const ref = doc(db, "albumPhotos", p.id);
          batch.update(ref, { isCover: false });
        }
      });

      // Set new cover in photo
      const newCoverRef = doc(db, "albumPhotos", photoId);
      batch.update(newCoverRef, { isCover: true });

      // Update album cover URLs in the album document
      const albumRef = doc(db, "privateAlbums", albumId);
      batch.update(albumRef, {
        coverImageUrl: photo.url,
        coverThumbnailUrl: photo.thumbnailUrl || photo.url,
        updatedAt: serverTimestamp(),
      });

      await batch.commit();
      return true;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ ACCESS MANAGEMENT ============

  /**
   * Share album with user
   */
  async shareAlbum(albumId, email, name, role, invitedBy) {
    try {
      if (!invitedBy) {
        throw new Error("invitedBy is required");
      }

      const existing = await this.getAlbumAccessByEmail(albumId, email);
      if (existing) {
        throw new Error(ERROR_MESSAGES.EMAIL_ALREADY_SHARED);
      }

      const permissions = getRolePermissions(role);
      const accessData = {
        albumId: albumId,
        email: email,
        name: name || email.split("@")[0],
        role: role,
        invitedBy: invitedBy,
        invitedAt: serverTimestamp(),
        status: ACCESS_STATUS.PENDING,
        lastAccessed: null,
        accessCount: 0,
        canDownload: permissions.download || false,
        canComment: permissions.comment || false,
        canUpload: permissions.upload || false,
        expiresAt: null,
        isActive: true,
      };

      const docRef = await addDoc(this.getAccessCollection(), accessData);
      const newDoc = await getDoc(docRef);

      // Update album access count
      const albumRef = doc(db, "privateAlbums", albumId);
      const albumSnap = await getDoc(albumRef);
      if (albumSnap.exists()) {
        const album = albumSnap.data();
        await updateDoc(albumRef, {
          accessCount: (album.accessCount || 0) + 1,
          updatedAt: serverTimestamp(),
        });
      }

      return {
        id: newDoc.id,
        ...this.convertDocData(newDoc.data()),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get album access list
   */
  async getAlbumAccess(albumId) {
    try {
      const q = query(
        this.getAccessCollection(),
        where("albumId", "==", albumId),
        where("isActive", "==", true),
      );
      const querySnapshot = await getDocs(q);
      const accessList = [];
      querySnapshot.forEach((doc) => {
        accessList.push({
          id: doc.id,
          ...this.convertDocData(doc.data()),
        });
      });
      return accessList;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get album access by email
   */
  async getAlbumAccessByEmail(albumId, email) {
    try {
      const q = query(
        this.getAccessCollection(),
        where("albumId", "==", albumId),
        where("email", "==", email),
        where("isActive", "==", true),
      );
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return null;
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...this.convertDocData(doc.data()),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Revoke access
   */
  async revokeAccess(accessId) {
    try {
      const accessRef = doc(db, "albumAccess", accessId);
      const accessSnap = await getDoc(accessRef);

      if (!accessSnap.exists()) {
        throw new Error("Access record not found");
      }

      const access = accessSnap.data();
      await updateDoc(accessRef, {
        status: ACCESS_STATUS.REVOKED,
        isActive: false,
        updatedAt: serverTimestamp(),
      });

      // Update album access count
      const albumRef = doc(db, "privateAlbums", access.albumId);
      const albumSnap = await getDoc(albumRef);
      if (albumSnap.exists()) {
        const album = albumSnap.data();
        await updateDoc(albumRef, {
          accessCount: Math.max(0, (album.accessCount || 0) - 1),
          updatedAt: serverTimestamp(),
        });
      }

      return accessId;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update access role
   */
  async updateAccessRole(accessId, role) {
    try {
      const permissions = getRolePermissions(role);
      const accessRef = doc(db, "albumAccess", accessId);
      await updateDoc(accessRef, {
        role: role,
        canDownload: permissions.download || false,
        canComment: permissions.comment || false,
        canUpload: permissions.upload || false,
        updatedAt: serverTimestamp(),
      });
      const updatedDoc = await getDoc(accessRef);
      return {
        id: updatedDoc.id,
        ...this.convertDocData(updatedDoc.data()),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ ACCESS LOGS ============

  /**
   * Log access activity
   */
  async logAccess(albumId, email, action, photoId = null) {
    try {
      const logData = {
        albumId: albumId,
        email: email,
        action: action,
        photoId: photoId,
        timestamp: serverTimestamp(),
        ipAddress: null,
        userAgent: null,
        deviceInfo: null,
        location: null,
      };

      const docRef = await addDoc(this.getAccessLogCollection(), logData);

      if (action === "view") {
        const albumRef = doc(db, "privateAlbums", albumId);
        const albumSnap = await getDoc(albumRef);
        if (albumSnap.exists()) {
          const album = albumSnap.data();
          await updateDoc(albumRef, {
            viewCount: (album.viewCount || 0) + 1,
            lastAccessed: serverTimestamp(),
          });
        }
      }

      const access = await this.getAlbumAccessByEmail(albumId, email);
      if (access) {
        const accessRef = doc(db, "albumAccess", access.id);
        await updateDoc(accessRef, {
          lastAccessed: serverTimestamp(),
          accessCount: (access.accessCount || 0) + 1,
        });
      }

      return { id: docRef.id, ...this.convertDocData(logData) };
    } catch (error) {
      console.error("Failed to log access:", error);
      return null;
    }
  }

  /**
   * Get access logs for album
   */
  async getAccessLogs(albumId, limitCount = 100) {
    try {
      const q = query(
        this.getAccessLogCollection(),
        where("albumId", "==", albumId),
        orderBy("timestamp", "desc"),
        limit(limitCount),
      );
      const querySnapshot = await getDocs(q);
      const logs = [];
      querySnapshot.forEach((doc) => {
        logs.push({
          id: doc.id,
          ...this.convertDocData(doc.data()),
        });
      });
      return logs;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ STATISTICS ============

  /**
   * Get album statistics
   */
  async getAlbumStats(albumId) {
    try {
      const album = await this.getAlbumById(albumId, true, true);

      let logs = [];
      try {
        logs = await this.getAccessLogs(albumId);
      } catch (logError) {
        console.error("Failed to fetch access logs:", logError);
      }

      const viewLogs = logs.filter((log) => log.action === "view");
      const downloadLogs = logs.filter((log) => log.action === "download");
      const commentLogs = logs.filter((log) => log.action === "comment");

      const uniqueViewers = new Set(viewLogs.map((log) => log.email));
      const uniqueDownloaders = new Set(downloadLogs.map((log) => log.email));

      const photoViews = {};
      viewLogs.forEach((log) => {
        if (log.photoId) {
          photoViews[log.photoId] = (photoViews[log.photoId] || 0) + 1;
        }
      });

      const mostViewed = Object.entries(photoViews)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([photoId, views]) => ({
          photoId,
          views,
          photo: album.photos?.find((p) => p.id === photoId),
        }));

      return {
        totalViews: viewLogs.length,
        totalDownloads: downloadLogs.length,
        totalComments: commentLogs.length,
        uniqueViewers: uniqueViewers.size,
        uniqueDownloaders: uniqueDownloaders.size,
        mostViewedPhotos: mostViewed,
        accessList: album.accessList || [],
        totalAccess: album.accessList?.length || 0,
        activeAccess:
          album.accessList?.filter((a) => a.status === "accepted").length || 0,
        pendingAccess:
          album.accessList?.filter((a) => a.status === "pending").length || 0,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get overall statistics
   */
  async getOverallStats() {
    try {
      const albums = await this.getAlbums();
      const allPhotos = [];

      for (const album of albums) {
        const photos = await this.getAlbumPhotos(album.id);
        allPhotos.push(...photos);
      }

      const activeAlbums = albums.filter((a) => a.status === "active");
      const archivedAlbums = albums.filter((a) => a.status === "archived");

      let totalSize = 0;
      let totalViews = 0;

      albums.forEach((album) => {
        totalSize += album.totalSize || 0;
        totalViews += album.viewCount || 0;
      });

      return {
        totalAlbums: albums.length,
        activeAlbums: activeAlbums.length,
        archivedAlbums: archivedAlbums.length,
        totalPhotos: allPhotos.length,
        totalSize: totalSize,
        totalViews: totalViews,
        averagePhotosPerAlbum:
          albums.length > 0 ? Math.round(allPhotos.length / albums.length) : 0,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ ERROR HANDLER ============

  handleError(error) {
    console.error("Firebase error:", error);
    if (error.code === "permission-denied") {
      return new Error("You don't have permission to perform this action.");
    }
    return new Error(error.message || "An error occurred");
  }
}

const privateAlbumService = new PrivateAlbumService();
export default privateAlbumService;
